import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import { TelegramLinkButton } from './TelegramLinkButton';
import { loadReminders, subscribeReminders, getReminderState, saveReminderChannels, setReminderLinked } from '../lib/auctionReminders';

export const REMINDER_LABELS = {
  it: { follow: 'Avvisami prima della fine', following: 'Ti avviso 1h prima', unfollow: 'Non avvisarmi più',
        followed: "Ok! Ti avviso 1 ora prima della fine dell'asta", unfollowed: 'Avviso fine asta disattivato',
        pickTitle: "Come vuoi ricevere l'avviso di fine asta?", email: 'Email', telegram: 'Telegram', both: 'Email + Telegram',
        tgHint: 'Collega Telegram per riceverlo lì. Finché non è collegato, ti scriviamo per email.',
        saved: 'Preferenza salvata', settingsTitle: 'Avviso fine asta',
        settingsDesc: "Le aste che segui (pulsante ⏰) e quelle nei Salvati ti avvisano circa 1 ora prima della fine, con l'offerta aggiornata.",
        proOnly: "L'avviso di fine asta è una funzione Pro.", login: 'Accedi per seguire le aste', ended: "L'asta è già terminata" },
  de: { follow: 'Vor Ende erinnern', following: 'Erinnerung 1 Std. vorher', unfollow: 'Nicht mehr erinnern',
        followed: 'Ok! Wir erinnern dich 1 Stunde vor Auktionsende', unfollowed: 'Auktions-Erinnerung deaktiviert',
        pickTitle: 'Wie möchtest du die Erinnerung erhalten?', email: 'E-Mail', telegram: 'Telegram', both: 'E-Mail + Telegram',
        tgHint: 'Verbinde Telegram, um sie dort zu erhalten. Bis dahin schreiben wir dir per E-Mail.',
        saved: 'Einstellung gespeichert', settingsTitle: 'Erinnerung vor Auktionsende',
        settingsDesc: 'Auktionen, denen du folgst (⏰), und gespeicherte Auktionen erinnern dich ca. 1 Stunde vor Ende – mit aktuellem Gebot.',
        proOnly: 'Die Auktions-Erinnerung ist eine Pro-Funktion.', login: 'Melde dich an, um Auktionen zu folgen', ended: 'Die Auktion ist bereits beendet' },
  fr: { follow: 'Me prévenir avant la fin', following: 'Rappel 1h avant', unfollow: 'Ne plus me prévenir',
        followed: "Ok ! On te prévient 1 heure avant la fin de l'enchère", unfollowed: 'Rappel de fin d\'enchère désactivé',
        pickTitle: 'Comment veux-tu recevoir le rappel ?', email: 'E-mail', telegram: 'Telegram', both: 'E-mail + Telegram',
        tgHint: 'Connecte Telegram pour le recevoir là. En attendant, on t\'écrit par e-mail.',
        saved: 'Préférence enregistrée', settingsTitle: "Rappel de fin d'enchère",
        settingsDesc: 'Les enchères suivies (⏰) et celles enregistrées te préviennent environ 1 heure avant la fin, avec l\'offre à jour.',
        proOnly: 'Le rappel de fin d\'enchère est une fonction Pro.', login: 'Connecte-toi pour suivre les enchères', ended: "L'enchère est déjà terminée" },
  en: { follow: 'Remind me before it ends', following: 'Reminder 1h before', unfollow: 'Stop reminding me',
        followed: "Done! We'll remind you 1 hour before the auction ends", unfollowed: 'Auction reminder turned off',
        pickTitle: 'How do you want to get the auction reminder?', email: 'Email', telegram: 'Telegram', both: 'Email + Telegram',
        tgHint: "Connect Telegram to get it there. Until then, we'll email you.",
        saved: 'Preference saved', settingsTitle: 'Auction ending reminder',
        settingsDesc: 'Auctions you follow (⏰) and saved auctions remind you about 1 hour before they end, with the latest bid.',
        proOnly: 'The auction reminder is a Pro feature.', login: 'Sign in to follow auctions', ended: 'The auction has already ended' },
};

export const useReminderState = () => {
  const { user } = useAuth() || {};
  const [s, setS] = useState(getReminderState());
  const uid = user?.user_id || null;
  useEffect(() => subscribeReminders(setS), []);
  useEffect(() => { loadReminders(uid); }, [uid]);
  return s;
};

const OPTIONS = [
  ['email', ['email'], Mail],
  ['telegram', ['telegram'], Send],
  ['both', ['email', 'telegram'], null],
];

// Scelta canali: Email / Telegram / entrambi + collegamento Telegram.
export const AuctionReminderChannels = ({ lang = 'it', onSaved }) => {
  const L = REMINDER_LABELS[lang] || REMINDER_LABELS.it;
  const { channels, linked } = useReminderState();
  const current = channels.length === 2 ? 'both' : channels[0] || 'email';
  const pick = async (id, value) => {
    try {
      await saveReminderChannels(value);
      toast.success(L.saved);
      onSaved?.(value);
    } catch { toast.error(L.proOnly); }
  };
  return (
    <div data-testid="auction-reminder-channels">
      <div className="inline-flex flex-wrap gap-1 p-0.5 rounded-lg bg-muted/70 border border-border/60">
        {OPTIONS.map(([id, value, Icon]) => (
          <button key={id} type="button" onClick={() => pick(id, value)}
            data-testid={`auction-reminder-channel-${id}`}
            className={`inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-md transition-colors ${
              current === id ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground hover:text-foreground'}`}>
            {Icon && <Icon size={12} />}{L[id]}
          </button>
        ))}
      </div>
      {current !== 'email' && (
        <div className="mt-3">
          <TelegramLinkButton lang={lang} onLinkedChange={setReminderLinked} />
          {!linked && <p className="text-xs text-muted-foreground mt-1">{L.tgHint}</p>}
        </div>
      )}
    </div>
  );
};
